import {forwardRef} from "react";
import {Input} from "../Input";

const CAR_NUMBER_PLACEHOLDER = "А000АА";
const LETTER_REGEXP = /[А-ЯЁ]/u;
const DIGIT_REGEXP = /\d/;

const formatCarNumberValue = (value: string) => {
  const normalizedValue = value.toUpperCase();
  const formattedValue: string[] = [];

  for (const char of normalizedValue) {
    const nextIndex = formattedValue.length;

    if (nextIndex >= 6) {
      break;
    }

    const expectsLetter = nextIndex === 0 || nextIndex >= 4;
    const matchesExpectedType = expectsLetter ? LETTER_REGEXP.test(char) : DIGIT_REGEXP.test(char);

    if (matchesExpectedType) {
      formattedValue.push(char);
    }
  }

  return formattedValue.join("");
};

type CarNumberInputProps = Omit<
  React.ComponentPropsWithoutRef<typeof Input>,
  "autoCapitalize" | "maxLength" | "spellCheck" | "type"
>;

export const CarNumberInput = forwardRef<HTMLInputElement, CarNumberInputProps>(
  ({defaultValue, onChange, placeholder = CAR_NUMBER_PLACEHOLDER, value, ...props}, ref) => {
    const handleChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
      const formattedValue = formatCarNumberValue(event.target.value);

      if (event.target.value !== formattedValue) {
        event.target.value = formattedValue;
      }

      onChange?.(event);
    };

    return (
      <Input
        {...props}
        ref={ref}
        type="text"
        autoCapitalize="characters"
        spellCheck={false}
        maxLength={6}
        placeholder={placeholder}
        defaultValue={
          typeof defaultValue === "string" ? formatCarNumberValue(defaultValue) : defaultValue
        }
        value={typeof value === "string" ? formatCarNumberValue(value) : value}
        onChange={handleChange}
      />
    );
  },
);

CarNumberInput.displayName = "CarNumberInput";
